import { Close, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import moment from "moment";
import { useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import ProtectedPermissionView from "../../components/ProtectedPermissionView";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import contractService from "../../services/contract";
import config from "../../utils/config";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function ContractList(props: any) {
  const [pageSize, setPageSize] = useState<number>(10);
  const [options, setOptions] = useState<any>({
    keyword: ""
  });
  const notification = useNotification();
  const deleteContract = useMutation(
    "delete-contract",
    contractService.deleteContract,
    {
      onSuccess: () => {
        listContract.refetch();
        notification.onOpen({
          message: "Contract was successfully deleted!",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        listContract.refetch();
        notification.onOpen({
          message:
            error?.response?.data?.message === undefined
              ? "Something went wrong"
              : error?.response?.data?.message,
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );
  const onDelete = (id: number) => {
    alert.onClose();
    deleteContract.mutate(id);
  };
  const onShare = (id: number) => {
    alert.onClose();
    contractService.sendFile(id)
      .then(() => {
        notification.onOpen({
          message: "File is successfully shared!",
          type: "success",
          position: "top",
        });
      })
      .catch(() => {
        notification.onOpen({
          message: "Something went wrong",
          type: "error",
          position: "top",
        });
      })
  };
  const alert = useAlert();
  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure want to delete this contract?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  const onClickShare = (id: number) => {
    alert.onOpen({
      title: "Confirm Share?",
      message: "Are you sure want to share file of this contract?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Confirm",
          onClick: () => onShare(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };
  const listContract = useQuery(
    ["contract-list", options],
    ({ queryKey }) => {
      return contractService.getAllContracts({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  const form = useForm({
    defaultValues: {
      keyword: "",
    },
  });

  const onHandleSearch = useCallback((values: { keyword: any }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
    }));
  }, []);

  const handleDownloadPDF = (params: any) => {
    contractService.downloadPDF(params);
  }

  return (
    <MainLayout>
      <Helmet>
        <title>Contract List</title>
      </Helmet>

      <Breadcrumb
        label={`Contract List`}
        breadcrumbs={[
          { label: "Contract Management", href: "/app/contract" },
        ]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 5, 6]}>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <Controller
              name="keyword"
              control={form.control}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Input Keyword here"
                  {...field}
                  {...config.onEnter(() => {
                    form.handleSubmit(onHandleSearch)();
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        onClick={() => {
                          if (field.value) {
                            field.onChange("");
                            setOptions((prev: any) => ({
                              ...prev,
                              keyword: "",
                            }));
                          }
                        }}
                      >
                        {field.value ? (
                          <Close color="error" sx={{ cursor: "pointer" }} />
                        ) : (
                          <Search />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button
              variant="outlined"
              onClick={form.handleSubmit(onHandleSearch)}
            >
              SEARCH
            </Button>
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          <ProtectedPermissionView acceptPermissions={[1, 5, 6]}>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                component={RouterLink}
                to="/app/contract/create"
              >
                Create Contract
              </Button>
            </Stack>
          </ProtectedPermissionView>
        </Box>

        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={listContract.isLoading}
            getRowId={(record) => get(record, "id")}
            rows={get(listContract, "data.data", [])}
            rowCount={get(listContract, "data.data", []).length}
            page={options.page_number}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageChange={(newPage) => {
              setOptions((prev: any) => ({ ...prev, page_number: newPage }));
            }}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              {
                field: "nama",
                headerName: "Nama",
                flex: 1,
                minWidth: 200,
                renderCell: (params) => {
                  return (
                    <Stack sx={{ backgroundColor: params.row.is_expiring ? '#EC5758' : 'none' }}>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "kota",
                headerName: "Kota",
                flex: 1,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "pic",
                headerName: "PIC",
                flex: 1,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "contact",
                headerName: "Contact",
                flex: 1,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "email",
                headerName: "Email",
                flex: 1,
                minWidth: 200,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "start_date",
                headerName: "Start Date",
                flex: 1,
                sortable: true,
                filterable: true,
                minWidth: 150,
                renderCell: (params) => {
                  if (!params.value) {
                    return "-";
                  }
                  return moment(params.value).format("DD/MM/YYYY");
                },
              },
              {
                field: "probation_date",
                headerName: "Probation Date",
                flex: 1,
                sortable: true,
                filterable: true,
                minWidth: 150,
                renderCell: (params) => {
                  if (!params.value) {
                    return "-";
                  }
                  return moment(params.value).format("DD/MM/YYYY");
                },
              },
              {
                field: "end_date",
                headerName: "End Date",
                flex: 1,
                sortable: true,
                filterable: true,
                minWidth: 150,
                renderCell: (params) => {
                  if (!params.value) {
                    return "-";
                  }
                  return (
                    <Stack sx={{ backgroundColor: params.row.is_expiring ? '#EC5758' : 'none' }}>
                      {moment(params.value).format("DD/MM/YYYY")}
                    </Stack>
                  )
                },
              },
              {
                field: "remaining_day",
                headerName: "Remaining days",
                flex: 1,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "status",
                headerName: "Status",
                flex: 1,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography
                        color={params.value === "2months" ? "grey" :
                          params.value === "probation" ? "green" :
                            params.value === "6months" ? "blue" :
                              params.value === "1year" ? "purple" :
                                params.value === "2years" ? "yellow" :
                                  params.value === "lifetime" ? "orange" : "red"
                        }>{params.value === "2months" ? "2 months" :
                          params.value === "probation" ? "probation (3 months)" :
                            params.value === "6months" ? "6 months" :
                              params.value === "1year" ? "1 Year" :
                                params.value === "2years" ? "2 Years" :
                                  params.value === "lifetime" ? "Lifetime" : "Closed"}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "pdf_file",
                headerName: "MoU",
                flex: 1,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      {params.value !== "" ? params.value?.split("|").map((val: string, idx: number) => (
                        <Button variant="contained" sx={{ mx: 1 }} onClick={() => handleDownloadPDF(params.value)}>
                          Download
                        </Button>
                      )) : "-"}
                    </Stack>
                  );
                },
              },
              {
                field: "actions",
                headerName: "Action",
                sortable: false,
                filterable: false,
                flex: 1,
                minWidth: 300,
                renderCell: (params) => {
                  return (
                    <Stack direction="row" spacing={1}>
                      <ProtectedPermissionView acceptPermissions={[1, 5, 6, 99]}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() => onClickShare(params.row.id)}
                        >
                          Share File
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          component={RouterLink}
                          to={`/app/contract/edit/${params.row.id}`}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => onClickDelete(params.row.id)}
                        >
                          Delete
                        </Button>
                      </ProtectedPermissionView>
                    </Stack>
                  );
                },
              },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
